import { HeadContent, Link, Outlet, Scripts, createRootRouteWithContext, useLocation, useRouter } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, type ReactNode } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { FloatingWhatsAppButton } from "@/components/shared/FloatingWhatsAppButton";
import { PublicChatbot } from "@/components/public/PublicChatbot";
import { CookieConsentBanner } from "@/components/privacy/CookieConsentBanner";
import NotFound from "@/pages/NotFound";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import appCss from "../styles.css?url";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { title: "MNRE | Premium Mumbai Properties by Mohnish Nagpal Real Estate" },
      { name: "description", content: "Discover premium residential and commercial properties across Mumbai with Mohnish Nagpal Real Estate. Search verified listings, compare projects and connect with our property experts." },
      { name: "author", content: "Mohnish Nagpal Real Estate" },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Mohnish Nagpal Real Estate" },
      { property: "og:title", content: "MNRE | Premium Mumbai Properties" },
      { property: "og:description", content: "Discover verified residential and commercial property listings across Mumbai." },
      { property: "og:url", content: "https://mnre.in/" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "MNRE | Premium Mumbai Properties" },
      { name: "twitter:description", content: "Discover verified residential and commercial property listings across Mumbai." },
      { name: "theme-color", content: "#99663c" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/webp", href: "/favicon.webp" },
      { rel: "alternate icon", type: "image/png", href: "/favicon.png" },
      { rel: "preload", as: "image", href: "/lovable-uploads/abe5bbca-52aa-4071-ba12-16af51cf5fac.webp", type: "image/webp" },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap" },
    ],
    scripts: [
      // After a new publish, old asset chunks disappear; reload once to fetch the fresh version instead of showing a blank page.
      { children: "(function(){var K='mnre-chunk-reload';function r(){try{var t=+sessionStorage.getItem(K)||0;if(Date.now()-t<10000)return;sessionStorage.setItem(K,String(Date.now()));}catch(e){}location.reload();}window.addEventListener('vite:preloadError',function(e){e.preventDefault();r();});window.addEventListener('unhandledrejection',function(e){var m=String(e.reason&&e.reason.message||e.reason||'');if(/Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(m))r();});})();" },
      { src: "https://www.googletagmanager.com/gtag/js?id=G-605EK1P52Y", async: true },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "RealEstateAgent",
              "@id": "https://mnre.in/#organization",
              "name": "Mohnish Nagpal Real Estate",
              "alternateName": "MNRE",
              "url": "https://mnre.in/",
              "logo": "https://mnre.in/lovable-uploads/abe5bbca-52aa-4071-ba12-16af51cf5fac.webp",
              "image": "https://mnre.in/blog-covers/mumbai-sealink-skyline.jpg",
              "telephone": "+917021011304",
              "email": "hello@mnre.in",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Ajanta Building, 3rd Road",
                "addressLocality": "Khar West",
                "addressRegion": "Mumbai, Maharashtra",
                "postalCode": "400052",
                "addressCountry": "IN"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": 19.07,
                "longitude": 72.8339
              },
              "areaServed": [
                "Bandra West, Mumbai",
                "Khar West, Mumbai",
                "Santacruz West, Mumbai",
                "Juhu, Mumbai",
                "Bandra Kurla Complex (BKC), Mumbai",
                "Worli, Mumbai",
                "Prabhadevi, Mumbai",
                "Mumbai Western Suburbs"
              ],
              "sameAs": [
                "https://www.facebook.com/mohnishnagpalOfficial",
                "https://www.instagram.com/mohnish.nagpal/",
                "https://mohnishnagpal.com/feed/"
              ],
              "founder": {
                "@type": "Person",
                "@id": "https://mnre.in/#person",
                "name": "Mohnish Nagpal",
                "jobTitle": "Founder & Principal Real Estate Advisor",
                "url": "https://mnre.in/about",
                "image": "https://mnre.in/lovable-uploads/abe5bbca-52aa-4071-ba12-16af51cf5fac.webp",
                "sameAs": [
                  "https://www.instagram.com/mohnish.nagpal/",
                  "https://www.facebook.com/mohnishnagpalOfficial"
                ]
              },
              "priceRange": "₹₹₹₹"
            },
            {
              "@type": "WebSite",
              "@id": "https://mnre.in/#website",
              "url": "https://mnre.in/",
              "name": "MNRE - Mohnish Nagpal Real Estate",
              "publisher": {
                "@id": "https://mnre.in/#organization"
              },
              "potentialAction": {
                "@type": "SearchAction",
                "target": "https://mnre.in/properties?q={search_term_string}",
                "query-input": "required name=search_term_string"
              }
            }
          ]
        })
      },
      { children: "window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments)}gtag('js',new Date());gtag('config','G-605EK1P52Y');" },
      { children: "!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','4507706419451193');fbq('track','PageView');" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: RootError,
});

function RootShell({ children }: { children: ReactNode }) {
  return <html lang="en" suppressHydrationWarning><head><HeadContent /></head><body>{children}<noscript><img height="1" width="1" style={{ display: "none" }} src="https://www.facebook.com/tr?id=4507706419451193&ev=PageView&noscript=1" alt="" /></noscript><Scripts /></body></html>;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useLocation({ select: (location) => location.pathname });
  const showFloatingWidgets = !pathname.startsWith("/admin");
  return <QueryClientProvider client={queryClient}><TooltipProvider><Toaster /><Sonner /><Outlet />{showFloatingWidgets && <><FloatingWhatsAppButton businessPhone="+917021011304" businessName="Mohnish Nagpal Real Estate" /><PublicChatbot /></>}<CookieConsentBanner /></TooltipProvider></QueryClientProvider>;
}

function RootError({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  useEffect(() => {
    const msg = String(error?.message ?? "");
    if (/Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(msg)) {
      try {
        const last = Number(sessionStorage.getItem("mnre-chunk-reload")) || 0;
        if (Date.now() - last > 10000) { sessionStorage.setItem("mnre-chunk-reload", String(Date.now())); window.location.reload(); return; }
      } catch { /* ignore */ }
    }
    console.error(error); reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return <main className="min-h-screen bg-background text-foreground flex items-center justify-center p-6"><section className="max-w-lg text-center"><h1 className="text-3xl font-bold mb-3">This page didn't load</h1><p className="text-muted-foreground mb-6">Please try again or return to the homepage.</p><div className="flex justify-center gap-3"><button className="rounded-md bg-primary px-4 py-2 text-primary-foreground" onClick={() => { router.invalidate(); reset(); }}>Try again</button><Link to="/" className="rounded-md border border-border px-4 py-2">Go home</Link></div></section></main>;
}